import * as React from 'react'
import { cn } from '@/lib/cn'

/**
 * Controls are a step taller below `md` so they are thumb-sized, and settle
 * back to desktop density from `md` up.
 */
const SIZE = {
  sm: { group: 'h-10 p-1 md:h-8 md:p-0.5', item: 'h-8 gap-1.5 px-2.5 text-[13px] md:h-7' },
  md: { group: 'h-11 p-1 md:h-9 md:p-0.5', item: 'h-9 gap-1.5 px-3 text-[13.5px] md:h-8' },
} as const

/**
 * Filter switch. Roving tabindex: the group takes one tab stop, arrows move
 * between options, exactly like a radio group — but the options stay buttons
 * with `aria-pressed`, because they toggle a view, not a form value.
 *
 * The group sits inside its own horizontal scroller. Five filter chips do not
 * fit across a 360px phone, and a control that is allowed to overflow its
 * parent is what makes the *document* scroll sideways — so the overflow is
 * caught here, at the widest element, rather than leaking to the page.
 */
export function Segmented<T extends string>(p: {
  value: T
  onChange: (v: T) => void
  options: { value: T; label: string; count?: number }[]
  size?: 'sm' | 'md'
  'aria-label'?: string
}) {
  const size = SIZE[p.size ?? 'sm']
  const refs = React.useRef<Array<HTMLButtonElement | null>>([])

  const selectedIndex = p.options.findIndex((o) => o.value === p.value)

  const move = (from: number, delta: number) => {
    const n = p.options.length
    if (n === 0) return
    const base = from < 0 ? 0 : from
    const next = (base + delta + n) % n
    const option = p.options[next]
    if (!option) return
    refs.current[next]?.focus()
    if (option.value !== p.value) p.onChange(option.value)
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault()
        move(index, 1)
        break
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault()
        move(index, -1)
        break
      case 'Home':
        e.preventDefault()
        move(-1, 0)
        break
      case 'End':
        e.preventDefault()
        move(-1, p.options.length - 1)
        break
      default:
        break
    }
  }

  return (
    <div
      className={cn(
        'max-w-full min-w-0 overflow-x-auto overscroll-x-contain',
        // The scrollbar would be a permanent grey line under a filter switch.
        '[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'
      )}
    >
      <div
        role="group"
        aria-label={p['aria-label']}
        className={cn(
          'inline-flex w-max items-center gap-0.5 rounded-md border border-line bg-s1',
          size.group
        )}
      >
        {p.options.map((option, index) => {
          const active = option.value === p.value
          const tabbable = selectedIndex < 0 ? index === 0 : active

          return (
            <button
              key={option.value}
              ref={(el) => {
                refs.current[index] = el
              }}
              type="button"
              aria-pressed={active}
              tabIndex={tabbable ? 0 : -1}
              onKeyDown={(e) => onKeyDown(e, index)}
              onClick={() => {
                if (!active) p.onChange(option.value)
              }}
              className={cn(
                'inline-flex flex-none items-center rounded-sm font-medium whitespace-nowrap',
                'transition-colors duration-100',
                size.item,
                active ? 'bg-accent-soft text-accent' : 'text-muted hover:bg-s3 hover:text-fg'
              )}
            >
              <span>{option.label}</span>
              {option.count !== undefined ? (
                <span
                  className={cn(
                    'tabular font-mono text-[11.5px]',
                    active ? 'text-accent/70' : 'text-faint'
                  )}
                >
                  {option.count}
                </span>
              ) : null}
            </button>
          )
        })}
      </div>
    </div>
  )
}
